import React, { useState, useMemo, useCallback, useRef } from "react";
import { Reading } from "../models/Reading"; // Adjust the import path as needed
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format, startOfWeek, startOfMonth, startOfYear, isWithinInterval, endOfWeek, addWeeks, startOfDay, endOfDay } from 'date-fns';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

type TimeRange = 'all' | 'yearly' | 'monthly' | 'weekly' | 'daily' | 'custom';

type ReadingsDisplayProps = {
    readingsData: Reading;
};

const ReadingsDisplay: React.FC<ReadingsDisplayProps> = ({ readingsData }) => {
    const [showReadings, setShowReadings] = useState<boolean>(false);
    const [timeRange, setTimeRange] = useState<TimeRange>('all');
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [customEndDate, setCustomEndDate] = useState<string>('');
    const [showChart, setShowChart] = useState<boolean>(true);
    const [currentWeekOffset, setCurrentWeekOffset] = useState<number>(0); // 0 = current week, -1 = previous week, etc.
    const [selectedDate, setSelectedDate] = useState<string>(''); // For daily view
    const [selectedWeekStart, setSelectedWeekStart] = useState<string>(''); // For custom weekly view

    // figure out which units exist
    const unit = readingsData?.unit || 'kw'; // default to 'kw' if unit is not set

    // Get the most recent reading date from the data
    const getMostRecentReadingDate = useCallback(() => {
        if (!readingsData?.readings) return new Date();
        
        const readings = Object.values(readingsData.readings) as any[];
        if (readings.length === 0) return new Date();
        
        // Find the most recent valid timestamp
        const validReadings = readings.filter(reading => {
            if (!reading.timestamp) return false;
            const date = new Date(reading.timestamp);
            return !isNaN(date.getTime());
        });
        
        if (validReadings.length === 0) return new Date();
        
        // Sort by timestamp and get the most recent
        validReadings.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        return new Date(validReadings[0].timestamp);
    }, [readingsData?.readings]);

    // Get default date string for date input based on current chart view
    const getDefaultDateForPicker = useCallback(() => {
        const mostRecent = getMostRecentReadingDate();
        switch (timeRange) {
            case 'daily':
                return format(mostRecent, 'yyyy-MM-dd');
            case 'monthly':
                return format(mostRecent, 'yyyy-MM-dd');
            default:
                return format(mostRecent, 'yyyy-MM-dd');
        }
    }, [getMostRecentReadingDate, timeRange]);

    // Get default week string for week input
    const getDefaultWeekForPicker = useCallback(() => {
        const mostRecent = getMostRecentReadingDate();
        const weekStart = startOfWeek(mostRecent);
        // Format as YYYY-Www where ww is the week number
        const year = format(weekStart, 'yyyy');
        const weekNumber = format(weekStart, 'II').padStart(2, '0'); // ISO week number
        return `${year}-W${weekNumber}`;
    }, [getMostRecentReadingDate]);

    // Store current filtered readings for tooltip access
    const currentFilteredReadingsRef = useRef<any[]>([]);

    // Process readings data for chart
    const chartData = useMemo(() => {
        if (!readingsData || !readingsData.readings || Object.keys(readingsData.readings).length === 0) {
            return {
                labels: [],
                datasets: [{
                    label: `Energy Reading (${unit})`,
                    data: [],
                    backgroundColor: 'rgba(97, 218, 251, 0.6)',
                    borderColor: 'rgba(97, 218, 251, 1)',
                    borderWidth: 1,
                }],
            };
        }
        
        // Debug: Log the readings data structure
        // console.log('Readings data structure:', readingsData.readings);
        
        const readings = Object.values(readingsData.readings) as any[];
        
        let filteredReadings = readings;

        // Filter by time range
        let startDate: Date | null = null;
        let endDate: Date | null = null;

        switch (timeRange) {
            case 'all':
                // Show all data - no filtering
                break;
            case 'yearly':
                // Use most recent reading date as anchor for yearly view
                const mostRecentDateYearly = getMostRecentReadingDate();
                startDate = startOfYear(mostRecentDateYearly);
                endDate = mostRecentDateYearly;
                console.log('Yearly view:', {
                    mostRecentDate: mostRecentDateYearly.toLocaleString(),
                    yearStart: startDate.toLocaleString(),
                    yearEnd: endDate.toLocaleString()
                });
                break;
            case 'monthly':
                // Use most recent reading date as anchor for monthly view
                const mostRecentDateMonthly = getMostRecentReadingDate();
                startDate = startOfMonth(mostRecentDateMonthly);
                endDate = mostRecentDateMonthly;
                console.log('Monthly view:', {
                    mostRecentDate: mostRecentDateMonthly.toLocaleString(),
                    monthStart: startDate.toLocaleString(),
                    monthEnd: endDate.toLocaleString()
                });
                break;
            case 'weekly':
                if (selectedWeekStart) {
                    // Custom week selection
                    startDate = startOfWeek(new Date(selectedWeekStart));
                    endDate = endOfWeek(new Date(selectedWeekStart));
                } else {
                    // Calculate the target week based on offset from most recent reading
                    const mostRecentDate = getMostRecentReadingDate();
                    const targetWeekStart = addWeeks(mostRecentDate, currentWeekOffset);
                    startDate = startOfWeek(targetWeekStart);
                    endDate = endOfWeek(targetWeekStart);
                }
                console.log('Weekly view:', {
                    selectedWeekStart,
                    currentWeekOffset,
                    weekStart: startDate.toLocaleString(),
                    weekEnd: endDate.toLocaleString()
                });
                break;
            case 'daily':
                if (selectedDate) {
                    // Custom day selection
                    const targetDate = new Date(selectedDate);
                    startDate = startOfDay(targetDate);
                    endDate = endOfDay(targetDate);
                } else {
                    // Use most recent reading date
                    const mostRecentDateDaily = getMostRecentReadingDate();
                    startDate = startOfDay(mostRecentDateDaily);
                    endDate = endOfDay(mostRecentDateDaily);
                }
                console.log('Daily view:', {
                    selectedDate,
                    dayStart: startDate.toLocaleString(),
                    dayEnd: endDate.toLocaleString()
                });
                break;
            case 'custom':
                if (customStartDate && customEndDate) {
                    startDate = new Date(customStartDate);
                    endDate = new Date(customEndDate);
                }
                break;
            default:
                // Default to showing all data
                break;
        }

        // Apply time range filtering only if dates are specified
        if (startDate && endDate) {
            filteredReadings = readings.filter(reading => {
                // Ensure reading has required properties
                if (!reading.timestamp || reading.value === undefined) {
                    console.warn('Invalid reading data:', reading);
                    return false;
                }
                
                const readingDate = new Date(reading.timestamp);
                // Check if date is valid
                if (isNaN(readingDate.getTime())) {
                    console.warn('Invalid timestamp:', reading.timestamp);
                    return false;
                }
                
                return isWithinInterval(readingDate, { start: startDate, end: endDate });
            });
        } else {
            // For 'all' time range, just validate the data
            filteredReadings = readings.filter(reading => {
                if (!reading.timestamp || reading.value === undefined) {
                    console.warn('Invalid reading data:', reading);
                    return false;
                }
                
                const readingDate = new Date(reading.timestamp);
                if (isNaN(readingDate.getTime())) {
                    console.warn('Invalid timestamp:', reading.timestamp);
                    return false;
                }
                
                return true;
            });
        }

        // Sort by timestamp
        filteredReadings.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        // Store filtered readings for tooltip access
        currentFilteredReadingsRef.current = filteredReadings;

        let labels: string[] = [];
        let values: number[] = [];

        if (timeRange === 'all') {
            // For "all data", aggregate by month to show monthly averages
            const monthlyData = new Map<string, { sum: number; count: number; readings: any[] }>();
            
            filteredReadings.forEach(reading => {
                const timestamp = new Date(reading.timestamp);
                const monthKey = format(timestamp, 'MMM yyyy'); // e.g., "Sep 2023"
                
                if (!monthlyData.has(monthKey)) {
                    monthlyData.set(monthKey, { sum: 0, count: 0, readings: [] });
                }
                
                const data = monthlyData.get(monthKey)!;
                const value = parseFloat(reading.value) || 0;
                data.sum += value;
                data.count += 1;
                data.readings.push(reading);
            });
            
            // Convert monthly data to arrays for chart
            const sortedMonths = Array.from(monthlyData.entries()).sort((a, b) => {
                // Sort by the first reading's timestamp in each month
                const dateA = new Date(a[1].readings[0].timestamp);
                const dateB = new Date(b[1].readings[0].timestamp);
                return dateA.getTime() - dateB.getTime();
            });
            
            labels = sortedMonths.map(([monthKey]) => monthKey);
            values = sortedMonths.map(([, data]) => data.sum / data.count); // Calculate average
            
        } else if (timeRange === 'daily') {
            // For daily view - show individual readings with time labels
            labels = filteredReadings.map((reading, index) => {
                const timestamp = new Date(reading.timestamp);
                let timeLabel = format(timestamp, 'HH:mm'); // Show time of day (e.g., "14:30")
                
                // If multiple readings have the same time label, add seconds or index to differentiate
                const sameTimeCount = labels.filter(label => label === timeLabel).length;
                if (sameTimeCount > 0) {
                    timeLabel = format(timestamp, 'HH:mm:ss'); // Add seconds for precision
                }
                
                return timeLabel;
            });
            
            values = filteredReadings.map(reading => parseFloat(reading.value) || 0);
            
            console.log('Daily view - individual readings:', {
                totalReadings: filteredReadings.length,
                timeLabels: labels.slice(0, 10), // Show first 10 for debugging
                values: values.slice(0, 10),
                dateRange: filteredReadings.length > 0 ? {
                    first: new Date(filteredReadings[0].timestamp).toLocaleString(),
                    last: new Date(filteredReadings[filteredReadings.length - 1].timestamp).toLocaleString()
                } : 'No readings'
            });
        } else if (timeRange === 'weekly' || timeRange === 'monthly') {
            // For weekly and monthly views - aggregate by day to show daily averages
            const dailyData = new Map<string, { sum: number; count: number; readings: any[] }>();
            
            filteredReadings.forEach(reading => {
                const timestamp = new Date(reading.timestamp);
                const dayKey = format(timestamp, 'yyyy-MM-dd'); // e.g., "2023-09-15"
                
                if (!dailyData.has(dayKey)) {
                    dailyData.set(dayKey, { sum: 0, count: 0, readings: [] });
                }
                
                const data = dailyData.get(dayKey)!;
                const value = parseFloat(reading.value) || 0;
                data.sum += value;
                data.count += 1;
                data.readings.push(reading);
            });
            
            // Convert daily data to arrays for chart
            const sortedDays = Array.from(dailyData.entries()).sort((a, b) => {
                return new Date(a[0]).getTime() - new Date(b[0]).getTime();
            });
            
            labels = sortedDays.map(([dayKey]) => {
                const date = new Date(dayKey);
                // Format based on time range for better readability
                switch (timeRange) {
                    case 'weekly':
                        return format(date, 'EEE MMM dd');
                    case 'monthly':
                        return format(date, 'MMM dd');
                    default:
                        return format(date, 'MMM dd');
                }
            });
            
            values = sortedDays.map(([, data]) => data.sum / data.count); // Calculate daily average
            

        } else {
            // For yearly and custom ranges, show individual data points or appropriate aggregation
            if (timeRange === 'yearly') {
                // For yearly view, aggregate by month
                const monthlyData = new Map<string, { sum: number; count: number; readings: any[] }>();
                
                filteredReadings.forEach(reading => {
                    const timestamp = new Date(reading.timestamp);
                    const monthKey = format(timestamp, 'MMM yyyy'); // e.g., "Sep 2023"
                    
                    if (!monthlyData.has(monthKey)) {
                        monthlyData.set(monthKey, { sum: 0, count: 0, readings: [] });
                    }
                    
                    const data = monthlyData.get(monthKey)!;
                    const value = parseFloat(reading.value) || 0;
                    data.sum += value;
                    data.count += 1;
                    data.readings.push(reading);
                });
                
                const sortedMonths = Array.from(monthlyData.entries()).sort((a, b) => {
                    const dateA = new Date(a[1].readings[0].timestamp);
                    const dateB = new Date(b[1].readings[0].timestamp);
                    return dateA.getTime() - dateB.getTime();
                });
                
                labels = sortedMonths.map(([monthKey]) => monthKey);
                values = sortedMonths.map(([, data]) => data.sum / data.count);
            } else {
                // For custom ranges, show individual readings with appropriate formatting
                labels = filteredReadings.map(reading => {
                    const timestamp = new Date(reading.timestamp);
                    const daysDiff = customStartDate && customEndDate ? 
                        (new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 3600 * 24) : 30;
                    if (daysDiff > 365) {
                        return format(timestamp, 'MMM yyyy');
                    } else if (daysDiff > 30) {
                        return format(timestamp, 'MMM dd');
                    } else {
                        return format(timestamp, 'MMM dd HH:mm');
                    }
                });
                
                values = filteredReadings.map(reading => parseFloat(reading.value) || 0);
            }
        }

        const datasetLabel = (() => {
            switch (timeRange) {
                case 'all':
                    return `Monthly Average Energy Reading (${unit})`;
                case 'yearly':
                    return `Monthly Average Energy Reading (${unit})`;
                case 'weekly':
                case 'monthly': 
                    return `Daily Average Energy Reading (${unit})`;
                case 'daily':
                    return `Energy Reading (${unit})`;
                default:
                    return `Energy Reading (${unit})`;
            }
        })();

        return {
            labels,
            datasets: [
                {
                    label: datasetLabel,
                    data: values,
                    backgroundColor: 'rgba(97, 218, 251, 0.6)',
                    borderColor: 'rgba(97, 218, 251, 1)',
                    borderWidth: 1,
                    // For daily view, use thinner bars to fit more data points
                    maxBarThickness: timeRange === 'daily' ? 15 : undefined,
                    // Ensure bars are visible even when there are many points
                    minBarLength: timeRange === 'daily' ? 2 : undefined,
                },
            ],
        };
    }, [readingsData, timeRange, customStartDate, customEndDate, unit, currentWeekOffset, selectedDate, selectedWeekStart, getMostRecentReadingDate]);

    // Early return check after hooks
    if (!readingsData || !readingsData.readings || Object.keys(readingsData.readings).length === 0) {
        return <div>No readings available.</div>;
    }

    const toggleReadings = () => {
        setShowReadings(!showReadings);
    };

    const toggleChart = () => {
        setShowChart(!showChart);
    };

    const goToPreviousWeek = () => {
        setCurrentWeekOffset(prev => prev - 1);
    };

    const goToNextWeek = () => {
        // Only allow going to next week if we're not already at the most recent week
        setCurrentWeekOffset(prev => prev + 1);
    };

    // Check if we can go to next week (not beyond the most recent data)
    const canGoToNextWeek = () => {
        return currentWeekOffset < 0; // Can only go forward if we're in the past
    };

    const resetToCurrentWeek = () => {
        setCurrentWeekOffset(0);
    };

    // Reset selections when switching time ranges
    const handleTimeRangeChange = (newTimeRange: TimeRange) => {
        if (newTimeRange !== 'weekly') {
            setCurrentWeekOffset(0);
            setSelectedWeekStart('');
        }
        if (newTimeRange !== 'daily' && newTimeRange !== 'monthly') {
            setSelectedDate('');
        }
        setTimeRange(newTimeRange);
    };

    // Get formatted week range for display
    const getWeekRangeDisplay = () => {
        if (timeRange === 'weekly') {
            const mostRecentDate = getMostRecentReadingDate();
            const targetWeekStart = addWeeks(mostRecentDate, currentWeekOffset);
            const weekStart = startOfWeek(targetWeekStart);
            const weekEnd = endOfWeek(targetWeekStart);
            return `${format(weekStart, 'MMM dd')} - ${format(weekEnd, 'MMM dd, yyyy')}`;
        }
        return '';
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    font: {
                        size: 12,
                    },
                },
            },
            title: {
                display: true,
                text: (() => {
                    const mostRecentDate = getMostRecentReadingDate();
                    switch (timeRange) {
                        case 'all':
                            return 'Energy Readings - Monthly Averages';
                        case 'weekly':
                            if (selectedWeekStart) {
                                const weekStart = startOfWeek(new Date(selectedWeekStart));
                                const weekEnd = endOfWeek(new Date(selectedWeekStart));
                                return `Energy Readings - Week of ${format(weekStart, 'MMM dd')} - ${format(weekEnd, 'MMM dd, yyyy')} (Daily Averages)`;
                            }
                            return `Energy Readings - Week of ${getWeekRangeDisplay()} (Daily Averages)`;
                        case 'monthly':
                            if (selectedDate) {
                                const targetMonth = new Date(selectedDate);
                                return `Energy Readings - ${format(targetMonth, 'MMMM yyyy')} (Daily Averages)`;
                            }
                            return `Energy Readings - ${format(mostRecentDate, 'MMMM yyyy')} (Daily Averages)`;
                        case 'daily':
                            if (selectedDate) {
                                return `Energy Readings - ${format(new Date(selectedDate), 'EEEE, MMMM dd, yyyy')} (Hourly)`;
                            }
                            return `Energy Readings - ${format(mostRecentDate, 'EEEE, MMMM dd, yyyy')} (Hourly)`;
                        case 'yearly':
                            return `Energy Readings - ${format(mostRecentDate, 'yyyy')} (Monthly Averages)`;
                        case 'custom':
                            if (customStartDate && customEndDate) {
                                return `Energy Readings - ${format(new Date(customStartDate), 'MMM dd')} to ${format(new Date(customEndDate), 'MMM dd, yyyy')}`;
                            }
                            return 'Energy Readings - Custom Range';
                    }
                })(),
                font: {
                    size: 16,
                    weight: 'bold' as const,
                },
                padding: 20,
            },
            tooltip: {
                callbacks: {
                    title: function(context: any) {
                        const dataIndex = context[0]?.dataIndex;
                        
                        if (timeRange === 'daily' && dataIndex !== undefined) {
                            // For daily view, show full timestamp from filtered readings
                            if (dataIndex < currentFilteredReadingsRef.current.length) {
                                const reading = currentFilteredReadingsRef.current[dataIndex];
                                if (reading && reading.timestamp) {
                                    return new Date(reading.timestamp).toLocaleString();
                                }
                            }
                            // Fallback to label if reading not found
                            return context[0]?.label || '';
                        } else if (timeRange === 'weekly' || timeRange === 'monthly') {
                            // For aggregated views, show just the date without specific time
                            const label = context[0]?.label;
                            if (timeRange === 'weekly') {
                                return `${label} (Daily Average)`;
                            } else if (timeRange === 'monthly') {
                                return `${label} (Daily Average)`;
                            }
                        } else if (timeRange === 'all' || timeRange === 'yearly') {
                            // For monthly aggregations, show month/year
                            const label = context[0]?.label;
                            return `${label} (Monthly Average)`;
                        }
                        
                        // Fallback to chart label for custom ranges
                        return context[0]?.label || '';
                    },
                    label: function(context: any) {
                        const value = context.parsed.y;
                        const formattedValue = typeof value === 'number' ? value.toFixed(2) : value;
                        
                        // For daily view, use just the value without "Daily Average" or "Monthly Average"
                        if (timeRange === 'daily') {
                            return `Energy Reading: ${formattedValue} ${unit}`;
                        }
                        
                        return `${context.dataset.label}: ${formattedValue} ${unit}`;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: `Energy Consumption (${unit})`,
                    font: {
                        size: 12,
                        weight: 'bold' as const,
                    },
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                },
            },
            x: {
                title: {
                    display: true,
                    text: timeRange === 'daily' ? 'Time of Day' : 'Time Period',
                    font: {
                        size: 12,
                        weight: 'bold' as const,
                    },
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                },
                ticks: {
                    font: {
                        size: timeRange === 'daily' ? 9 : 10,
                    },
                    maxRotation: timeRange === 'daily' ? 90 : 45,
                    minRotation: timeRange === 'daily' ? 45 : 0,
                    // For daily view, ensure all points are shown
                    maxTicksLimit: timeRange === 'daily' ? undefined : undefined,
                    autoSkip: timeRange === 'daily' ? false : true,
                },
                // For daily view, ensure proper spacing of time labels
                ...(timeRange === 'daily' && {
                    type: 'category' as const,
                    offset: true,
                }),
            },
        },
    };

    return (
        <div>
            {/* Header Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <h2>Readings</h2>
                <button 
                    onClick={toggleChart}
                    style={{
                        padding: '5px 10px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                    }}
                >
                    {showChart ? 'Hide Chart' : 'Show Chart'}
                </button>
                <span style={{ fontSize: '14px', color: '#666' }}>
                    ({Object.keys(readingsData.readings).length} readings total)
                </span>
            </div>

            {/* Chart Section */}
            {showChart && (
                <div style={{ 
                    marginBottom: '30px', 
                    padding: '20px', 
                    backgroundColor: '#f9f9f9', 
                    borderRadius: '8px',
                    border: '1px solid #ddd'
                }}>
                    {/* Time Range Controls */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '15px', 
                        marginBottom: '20px',
                        flexWrap: 'wrap'
                    }}>
                        <label style={{ fontWeight: 'bold' }}>Time Range:</label>
                        
                        <select 
                            value={timeRange} 
                            onChange={(e) => handleTimeRangeChange(e.target.value as TimeRange)}
                            style={{
                                padding: '5px 10px',
                                borderRadius: '4px',
                                border: '1px solid #ccc'
                            }}
                        >
                            <option value="all">All Data</option>
                            <option value="weekly">Weekly View</option>
                            <option value="monthly">Monthly View</option>
                            <option value="daily">Daily View</option>
                            <option value="yearly">Yearly View</option>
                            <option value="custom">Custom Range</option>
                        </select>

                        {/* Weekly Navigation Controls */}
                        {timeRange === 'weekly' && (
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <button 
                                    onClick={goToPreviousWeek}
                                    style={{
                                        padding: '5px 10px',
                                        backgroundColor: '#f0f0f0',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                    title="Previous Week"
                                >
                                    ← Previous
                                </button>
                                
                                <span style={{ 
                                    fontSize: '14px', 
                                    fontWeight: 'bold',
                                    minWidth: '200px',
                                    textAlign: 'center'
                                }}>
                                    {getWeekRangeDisplay()}
                                </span>
                                
                                <button 
                                    onClick={goToNextWeek}
                                    disabled={!canGoToNextWeek()}
                                    style={{
                                        padding: '5px 10px',
                                        backgroundColor: !canGoToNextWeek() ? '#e0e0e0' : '#f0f0f0',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        cursor: !canGoToNextWeek() ? 'not-allowed' : 'pointer',
                                        fontSize: '12px',
                                        opacity: !canGoToNextWeek() ? 0.6 : 1
                                    }}
                                    title={!canGoToNextWeek() ? "Already showing most recent week" : "Next Week"}
                                >
                                    Next →
                                </button>
                                
                                {currentWeekOffset !== 0 && (
                                    <button 
                                        onClick={resetToCurrentWeek}
                                        style={{
                                            padding: '5px 10px',
                                            backgroundColor: '#61dafb',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                        title="Go to Current Week"
                                    >
                                        Current Week
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Custom Week Picker */}
                        {timeRange === 'weekly' && (
                            <div style={{ 
                                display: 'flex', 
                                gap: '10px', 
                                alignItems: 'center',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Or select a specific week:</label>
                                <input
                                    type="week"
                                    value={selectedWeekStart}
                                    placeholder={getDefaultWeekForPicker()}
                                    onChange={(e) => {
                                        setSelectedWeekStart(e.target.value);
                                        setCurrentWeekOffset(0); // Reset navigation offset when custom week is selected
                                    }}
                                    style={{
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        fontSize: '12px'
                                    }}
                                />
                                {selectedWeekStart && (
                                    <button
                                        onClick={() => setSelectedWeekStart('')}
                                        style={{
                                            padding: '5px 8px',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                        title="Clear custom week selection"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Daily Date Picker */}
                        {timeRange === 'daily' && (
                            <div style={{ 
                                display: 'flex', 
                                gap: '10px', 
                                alignItems: 'center',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Select date:</label>
                                <input
                                    type="date"
                                    value={selectedDate || getDefaultDateForPicker()}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    style={{
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        fontSize: '12px'
                                    }}
                                />
                                {selectedDate && (
                                    <button
                                        onClick={() => setSelectedDate('')}
                                        style={{
                                            padding: '5px 8px',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                        title="Clear date selection (show latest reading day)"
                                    >
                                        ✕
                                    </button>
                                )}
                                {!selectedDate && (
                                    <span style={{ 
                                        fontSize: '12px', 
                                        color: '#666',
                                        fontStyle: 'italic'
                                    }}>
                                        Showing latest reading day
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Monthly Date Picker */}
                        {timeRange === 'monthly' && (
                            <div style={{ 
                                display: 'flex', 
                                gap: '10px', 
                                alignItems: 'center',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Or select a specific month:</label>
                                <input
                                    type="month"
                                    value={selectedDate ? format(new Date(selectedDate), 'yyyy-MM') : format(getMostRecentReadingDate(), 'yyyy-MM')}
                                    onChange={(e) => setSelectedDate(e.target.value ? `${e.target.value}-01` : '')}
                                    style={{
                                        padding: '5px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        fontSize: '12px'
                                    }}
                                />
                                {selectedDate && (
                                    <button
                                        onClick={() => setSelectedDate('')}
                                        style={{
                                            padding: '5px 8px',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                        title="Clear month selection (show month of latest reading)"
                                    >
                                        ✕
                                    </button>
                                )}
                                {!selectedDate && (
                                    <span style={{ 
                                        fontSize: '12px', 
                                        color: '#666',
                                        fontStyle: 'italic'
                                    }}>
                                        Showing month of latest reading
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Custom Range Controls */}
                        {timeRange === 'custom' && (
                            <div style={{ 
                                display: 'flex', 
                                gap: '15px', 
                                alignItems: 'center',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 'bold' }}>From:</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        style={{
                                            padding: '5px',
                                            borderRadius: '4px',
                                            border: '1px solid #ccc',
                                            fontSize: '12px'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 'bold' }}>To:</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        style={{
                                            padding: '5px',
                                            borderRadius: '4px',
                                            border: '1px solid #ccc',
                                            fontSize: '12px'
                                        }}
                                    />
                                </div>
                                {customStartDate && customEndDate && (
                                    <span style={{ 
                                        fontSize: '12px', 
                                        color: '#666',
                                        fontStyle: 'italic'
                                    }}>
                                        {(() => {
                                            const start = new Date(customStartDate);
                                            const end = new Date(customEndDate);
                                            const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
                                            return `${days} day${days !== 1 ? 's' : ''} selected`;
                                        })()}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Chart */}
                    <div style={{ 
                        height: timeRange === 'daily' && chartData.labels.length > 20 ? '500px' : '400px',
                        overflowX: timeRange === 'daily' && chartData.labels.length > 30 ? 'auto' : 'visible'
                    }}>
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                </div>
            )}

            {/* Data Summary and Show Details Button */}
            <div>
                <h2>Individual Readings Results</h2>
                
                {/* Data Summary */}
                <div style={{ 
                    display: 'flex', 
                    gap: '20px', 
                    marginBottom: '15px',
                    padding: '10px',
                    backgroundColor: '#f0f8ff',
                    borderRadius: '4px',
                    fontSize: '14px',
                    flexWrap: 'wrap'
                }}>
                    <span><strong>Total Readings:</strong> {Object.keys(readingsData.readings).length}</span>
                    {chartData.datasets[0].data.length > 0 && (
                        <>
                            <span><strong>Chart Points:</strong> {chartData.datasets[0].data.length}</span>
                            {timeRange === 'all' && (
                                <span><strong>Aggregation:</strong> Monthly Averages</span>
                            )}
                            <span><strong>Max Value:</strong> {Math.max(...(chartData.datasets[0].data as number[])).toFixed(2)} {unit}</span>
                            <span><strong>Min Value:</strong> {Math.min(...(chartData.datasets[0].data as number[])).toFixed(2)} {unit}</span>
                        </>
                    )}
                </div>
            
                <div style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    marginBottom: '20px',
                    justifyContent: 'center'
                }}>
                    <button 
                        onClick={toggleReadings}
                        style={{
                            padding: '8px 15px',
                            backgroundColor: '#61dafb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        {showReadings ? 'Hide Details' : 'Show Details'}
                    </button>
                </div>
            </div>
            
            {showReadings && (
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                        <tr>
                            <th style={{ border: "1px solid #ccc", padding: "8px", backgroundColor: '#f5f5f5' }}>Timestamp</th>
                            <th style={{ border: "1px solid #ccc", padding: "8px", backgroundColor: '#f5f5f5' }}>
                                Value ({unit})
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.values(readingsData.readings).map((reading: any, idx) => (
                            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f9f9f9' : 'white' }}>
                                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                                    {new Date(reading.timestamp).toLocaleString()}
                                </td>
                                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                                    {reading.value}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {!showReadings && (
                <div style={{ 
                    padding: '20px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '4px',
                    textAlign: 'center',
                    color: '#666'
                }}>
                    Click "Show Details" to view all readings data
                </div>
            )}
        </div>
    );
};

export default ReadingsDisplay;
